import { clearAllBrowserStorage, clearToken } from "../services/storageService";
import { keycloak } from "./keycloak";
import { APP_LOGGED_OUT_KEY } from "./session/authSession.constants";
import { clearAuthSessionQuery } from "./session/authQueryCache";
import { buildGatewayLoginUrlWithAppContext } from "./session/gatewayLoginContext";
import {
  isSsoPrefetchRequest,
  notifySsoPrefetchComplete,
} from "./session/ssoPrefetch";
import {
  resolveGatewayBaseUrl,
  resolveGatewayUpstreamHttpUrl,
} from "../runtime/loadSharedDashboardConfig";

/** Prevent focus/visibility/StrictMode from spamming select-app + login redirects. */
let ssoLoginRedirectStarted = false;

function gatewayBaseUrl(): string {
  return resolveGatewayBaseUrl() || "http://localhost:4702";
}

export function gatewayLoginUrl(): string {
  const fromEnv = (process.env.REACT_APP_LOGIN_URL ?? "").trim();
  if (fromEnv) return fromEnv;
  return `${gatewayBaseUrl()}/login`;
}

/** Real gateway host for SSO logout — not SPA origin (nginx does not proxy /logout). */
function gatewayLogoutBaseUrl(): string {
  const loginUrl = (process.env.REACT_APP_LOGIN_URL ?? "").trim();
  if (loginUrl) {
    try {
      return new URL(loginUrl).origin;
    } catch {
      // fall through
    }
  }
  return resolveGatewayUpstreamHttpUrl() || gatewayBaseUrl();
}

function buildGatewayLogoutUrl(): string {
  const returnUrl =
    typeof window !== "undefined" ? `${window.location.origin}/` : "/";
  return `${gatewayLogoutBaseUrl()}/logout?url=${encodeURIComponent(returnUrl)}`;
}

export function setAppLoggedOutFlag(): void {
  try {
    localStorage.setItem(APP_LOGGED_OUT_KEY, "true");
  } catch {
    // ignore
  }
}

export function hasAppLoggedOutFlag(): boolean {
  try {
    return localStorage.getItem(APP_LOGGED_OUT_KEY) === "true";
  } catch {
    return false;
  }
}

export function clearAppLoggedOutFlag(): void {
  try {
    localStorage.removeItem(APP_LOGGED_OUT_KEY);
  } catch {
    // ignore
  }
}

/** Clear session storage but keep the app-local logout marker. */
export function clearAppSessionStorage(): void {
  const loggedOut = hasAppLoggedOutFlag();
  clearAllBrowserStorage();
  if (loggedOut) {
    setAppLoggedOutFlag();
  }
}

export function redirectToLogin(): void {
  void redirectToSsoLogin({ forceLogin: hasAppLoggedOutFlag() });
}

export async function redirectToKeycloakLogin(): Promise<void> {
  await redirectToSsoLogin({ forceLogin: true });
}

/** Keycloak SSO — silent when Keycloak session exists; forceLogin after app logout. */
export async function redirectToSsoLogin(options?: {
  forceLogin?: boolean;
}): Promise<void> {
  if (typeof window === "undefined") return;
  if (isSsoPrefetchRequest()) {
    notifySsoPrefetchComplete();
    return;
  }
  if (ssoLoginRedirectStarted) return;
  ssoLoginRedirectStarted = true;

  try {
    await registerPostLoginTargetForThisApp();
    window.location.replace(
      buildGatewayLoginUrlWithAppContext({
        forceLogin: options?.forceLogin ?? false,
      }),
    );
  } catch {
    ssoLoginRedirectStarted = false;
  }
}

export async function registerPostLoginTargetForThisApp(): Promise<void> {
  if (typeof window === "undefined") return;
  if (isSsoPrefetchRequest()) return;

  const base = gatewayBaseUrl();
  const appUrl = window.location.origin;
  const appName = (
    process.env.REACT_APP_APPLICATION_NAME ??
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID ??
    document.title ??
    "Niadit"
  ).trim();
  const keycloakClientId = (process.env.REACT_APP_KEYCLOAK_CLIENT_ID ?? "").trim();
  const applicationId = (process.env.REACT_APP_APPLICATION_ID ?? "").trim();

  try {
    await fetch(`${base}/login/select-app`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: AbortSignal.timeout(1500),
      body: JSON.stringify({
        url: appUrl,
        name: appName,
        keycloakClientId,
        applicationId,
      }),
    });
  } catch {
    // Best-effort only.
  }
}

function resetKeycloakSession(): void {
  try {
    keycloak.clearToken?.();
  } catch {
    // ignore
  }
  keycloak.authenticated = false;
  keycloak.token = undefined;
  keycloak.refreshToken = undefined;
  keycloak.idToken = undefined;
}

/** Per-app logout — top-level gateway /logout terminates Keycloak SSO for niadit. */
export async function logoutFromThisApp(): Promise<void> {
  if (typeof window === "undefined") return;

  setAppLoggedOutFlag();
  clearToken();
  clearAppSessionStorage();
  clearAuthSessionQuery();
  resetKeycloakSession();
  window.location.href = buildGatewayLogoutUrl();
}

/** Explicit SSO sign-in — silent unless this app was explicitly logged out. */
export async function signInViaSso(): Promise<void> {
  await redirectToSsoLogin({ forceLogin: hasAppLoggedOutFlag() });
}

/** Token refresh failure — sign out of this app only. */
export async function logoutOnSessionExpired(): Promise<void> {
  clearAllBrowserStorage();
  await logoutFromThisApp();
}

// Keycloak adapter may still invoke this on auth logout events.
export function onKeycloakAuthLogout(): void {
  setAppLoggedOutFlag();
  clearAppSessionStorage();
}

/** @deprecated Use logoutFromThisApp */
export async function logoutFromKeycloak(): Promise<void> {
  await logoutFromThisApp();
}
